import React, { useState } from "react";
import {
  ContentContainer,
  LoadingIndicator,
  PageTitle,
  RootContainer,
  TitleContainer,
} from "@components/ui/PageBaseComponents";
import Api from "@services/Api";
import { PaymentStatus, Plan, Sale, SaleType, SelectOption, Store } from "@util/interfaces";
import { toastError } from "@util/functions";
import { confirmAlert } from "react-confirm-alert";
import Select from "@components/ui/Select";
import AsyncSelect from "@components/ui/Select/Async";
import { useHistory } from "react-router";

const NewSaleScreen: React.FC = () => {
  const history = useHistory();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number>();
  const [selectedPeriod, setSelectedPeriod] = useState<string>();
  const [selectedSaleType, setSelectedSaleType] = useState<string>();
  const [selectedStoreId, setSelectedStoreId] = useState<number>();
  const [salePaymentLink] = useState<string>();
  const [pendingSale, setPendingSale] = useState<Sale>();
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [loadingPendingSale, setLoadingPendingSale] = useState(false);
  const [loadingSaleHistory, setLoadingSaleHistory] = useState(false);
  const [saleHistoryChecked, setSaleHistoryChecked] = useState(false);
  const [hasPreviousPaidActiveSale, setHasPreviousPaidActiveSale] = useState(false);
  const saleHistoryRequestId = React.useRef(0);

  React.useEffect(() => {
    fetchPlans();
  }, []);

  React.useEffect(() => {
    if (selectedStoreId) {
      fetchPendingSale();
      fetchSaleHistory(selectedStoreId);
    } else {
      saleHistoryRequestId.current += 1;
      setPendingSale(undefined);
      setSaleHistoryChecked(false);
      setHasPreviousPaidActiveSale(false);
    }
    // eslint-disable-next-line
  }, [selectedStoreId]);

  const loadStores = async (inputValue: string): Promise<SelectOption[]> => {
    try {
      const { data } = await Api.get(`/store?name=${inputValue}`);
      return data.stores?.map?.((store: Store) => ({ value: store.id, label: store.name })) || [];
    } catch (error) {
      toastError(error);
      return [];
    }
  };

  const fetchPendingSale = async () => {
    try {
      setLoadingPendingSale(true);
      const { data } = await Api.get(`/store/${selectedStoreId}/pending_sale`);
      setPendingSale(data.sale);
      setLoadingPendingSale(false);
    } catch (error) {
      setLoadingPendingSale(false);
      toastError(error);
    }
  };

  const fetchSaleHistory = async (storeId: number) => {
    const requestId = ++saleHistoryRequestId.current;

    try {
      setLoadingSaleHistory(true);
      setSaleHistoryChecked(false);
      setHasPreviousPaidActiveSale(false);

      const { data } = await Api.get(`/sale`);
      const storeSales: Sale[] = data.sales?.filter?.((sale: Sale) => sale.store?.id === storeId) ?? [];
      const hasPaidActiveSale = storeSales.some((sale) => {
        const wasActive = Boolean(sale.activated_at || sale.expires_at || sale.expired_at);
        const hasPaidPayment = sale.payments?.some((payment) => payment.status === PaymentStatus.PAID);

        return wasActive && hasPaidPayment;
      });

      if (requestId !== saleHistoryRequestId.current) return;
      setHasPreviousPaidActiveSale(hasPaidActiveSale);
      setSaleHistoryChecked(true);
    } catch (error) {
      if (requestId === saleHistoryRequestId.current) toastError(error);
    } finally {
      if (requestId === saleHistoryRequestId.current) setLoadingSaleHistory(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await Api.get(`/plan`);
      setPlans(data.plans);
      setLoadingPlans(false);
    } catch (error) {
      setLoadingPlans(false);
      toastError(error);
    }
  };

  const validateSubmit = () => {
    if (selectedSaleType === "upgrade" || selectedSaleType === "renewal") {
      confirmAlert({
        title: "Atenção",
        message: `Ao criar uma renovação ou atualização, qualquer venda ativa será 
        expirada com a data atual, e a loja irá para o plano Free até que a nova venda seja confirmada. 
        Deseja continuar?`,
        buttons: [
          {
            label: "Sim",
            onClick: async () => {
              handleSubmit();
            },
          },
          {
            label: "Cancelar",
            onClick: () => {},
          },
        ],
      });
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlanId || !selectedPeriod || !selectedSaleType || !selectedStoreId) {
      toastError("Preencha todos os dados para continuar");
      return;
    }
    if (
      selectedSaleType === SaleType.FIRST_TIME &&
      (loadingSaleHistory || !saleHistoryChecked || hasPreviousPaidActiveSale)
    ) {
      toastError("Primeira venda indisponível para este estabelecimento");
      return;
    }
    setSubmittingForm(true);

    try {
      const { data } = await Api.post(`/sale`, {
        plan_id: selectedPlanId,
        period: selectedPeriod,
        sale_type: selectedSaleType,
        store_id: selectedStoreId,
      });
      history.push(`/vendas/${data.sale.id}/pagamentos`);
    } catch (error) {
      setSubmittingForm(false);
      toastError(error);
    }
  };

  const periodsOptions = [
    { value: "monthly", label: "Mensal" },
    { value: "semesterly", label: "Semestral" },
    { value: "yearly", label: "Anual" },
  ];

  const saleTypesOptions = [
    { value: SaleType.FIRST_TIME, label: "Primeira venda" },
    { value: SaleType.RENEWAL, label: "Renovação" },
    { value: SaleType.UPGRADE, label: "Atualização" },
  ];

  const isFirstSaleDisabled =
    !selectedStoreId || loadingSaleHistory || !saleHistoryChecked || hasPreviousPaidActiveSale;

  if (loadingPlans) {
    return (
      <RootContainer>
        <TitleContainer>
          <PageTitle>Cadastrar venda</PageTitle>
        </TitleContainer>
        <ContentContainer>
          <LoadingIndicator />
        </ContentContainer>
      </RootContainer>
    );
  }

  return (
    <RootContainer>
      <TitleContainer>
        <PageTitle>Cadastrar venda</PageTitle>
      </TitleContainer>
      <ContentContainer>
        <div className="row m-0 p-0">
          <div className="mb-4 col-md-3">
            <label htmlFor="storeName">estabelecimento</label>
            <AsyncSelect
              placeholder="Buscar estabelecimento"
              loadingMessage={() => "Buscando..."}
              noOptionsMessage={() => "Sem resultados"}
              cacheOptions
              defaultOptions
              loadOptions={loadStores}
              onChange={(option: any) => {
                setSelectedSaleType(undefined);
                setSelectedStoreId(option?.value);
              }}
            />
          </div>
          <div className="mb-4 col-md-3">
            <label htmlFor="plan">plano</label>
            <Select
              placeholder="Selecionar plano"
              options={plans.map((plan) => ({ value: plan.id, label: plan.name }))}
              onChange={(option: any) => {
                setSelectedPlanId(option?.value);
              }}
            />
          </div>
          <div className="mb-4 col-md-3">
            <label htmlFor="period">período</label>
            <Select
              placeholder="Selecionar período"
              options={periodsOptions}
              onChange={(option: any) => {
                setSelectedPeriod(option?.value);
              }}
            />
          </div>
          <div className="mb-4 col-md-3">
            <label htmlFor="sale_type">tipo de venda</label>
            <Select
              placeholder="Selecionar tipo de venda"
              options={saleTypesOptions}
              value={saleTypesOptions.find((option) => option.value === selectedSaleType) ?? null}
              isOptionDisabled={(option) => option.value === SaleType.FIRST_TIME && isFirstSaleDisabled}
              onChange={(option: any) => {
                setSelectedSaleType(option?.value);
              }}
            />
            {hasPreviousPaidActiveSale && (
              <small className="text-muted">
                Primeira venda indisponível: este estabelecimento já possui uma venda ativada e paga.
              </small>
            )}
            {selectedStoreId && !loadingSaleHistory && !saleHistoryChecked && (
              <small className="text-danger">Não foi possível verificar o histórico de vendas.</small>
            )}
          </div>

          {!loadingPendingSale && !pendingSale && !salePaymentLink && (
            <div className="col-12 mt-4">
              <button
                className="btn btn-primary"
                onClick={validateSubmit}
                disabled={submittingForm || pendingSale !== undefined}
              >
                {submittingForm ? "Criando..." : "Criar"}
              </button>
            </div>
          )}
        </div>
      </ContentContainer>
    </RootContainer>
  );
};

export default NewSaleScreen;
